import { FormEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Button,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { surepassTheme } from '../../theme/surepass';
import { AgencyQuestionKind } from '../../utils/types';
import type { WebApiResult } from '../../networkRequest/api';
import type {
  AgencyQuestionSummary,
  Checkpoint,
  CheckpointFields,
} from '../../networkRequest/checkpointsService';

const TITLE_ID = 'checkpoint-dialog-title';
const NAME_ID = 'checkpoint-name';
const DESCRIPTION_ID = 'checkpoint-description';

/**
 * The dialog paper renders as the form. `noValidate` turns off the browser's own
 * checks: Name is `required` (for its asterisk and aria-required), and without it
 * the browser blocks the submit with its own bubble before our message can show.
 * MUI's paper slot types don't list form attributes, so they're passed this way.
 */
const FORM_PAPER_PROPS = { component: 'form', noValidate: true } as const;

/**
 * The theme's scrollbar (Scrollbar page), so it stays visible on a Mac rather than
 * fading out. The theme sets it app-wide through MuiCssBaseline, which this app
 * doesn't render, so the dialog applies the theme's own styles to everything
 * inside its paper — including the scrolling DialogContent.
 */
const scrollbarStyles = (theme: Theme) => {
  const overrides = surepassTheme.components?.MuiCssBaseline?.styleOverrides;
  // The theme's override is typed as any CSS, which `sx` doesn't accept as-is.
  return (typeof overrides === 'function' ? overrides(theme) : overrides) as SystemStyleObject<Theme>;
};

/**
 * Lines a helper text up with its checkbox's label: the 42px checkbox less the
 * FormControlLabel's -11px start margin.
 */
const CHECKBOX_LABEL_INSET = '31px';

type CheckpointFormValues = {
  name: string;
  description: string;
  pin_required: boolean;
  check_in_enabled: boolean;
  /** agency_question ids whose rule is enabled. */
  enabledQuestionIds: string[];
};

/**
 * Editing starts from the checkpoint. Adding starts from the defaults: no PIN,
 * check-in tracked, and only the official capacity question asked.
 */
const getFormValues = (
  checkpoint: Checkpoint | null,
  agencyQuestions: AgencyQuestionSummary[],
): CheckpointFormValues => {
  let values: CheckpointFormValues;
  if (checkpoint) {
    values = {
      name: checkpoint.name,
      description: checkpoint.description ?? '',
      pin_required: checkpoint.pin_required,
      check_in_enabled: checkpoint.check_in_enabled,
      enabledQuestionIds: checkpoint.checkpoint_rules
        .filter((rule) => rule.enabled)
        .map((rule) => rule.agency_question.id),
    };
  } else {
    values = {
      name: '',
      description: '',
      pin_required: false,
      check_in_enabled: true,
      enabledQuestionIds: agencyQuestions
        .filter((question) => question.kind === AgencyQuestionKind.OFFICIAL_CAPACITY)
        .map((question) => question.id),
    };
  }
  return values;
};

/**
 * What's sent to the API: trimmed, with an empty description sent as null, and
 * every question on or off.
 */
const getCheckpointFields = (
  values: CheckpointFormValues,
  questions: AgencyQuestionSummary[],
): CheckpointFields => ({
  name: values.name.trim(),
  description: values.description.trim() || null,
  pin_required: values.pin_required,
  check_in_enabled: values.check_in_enabled,
  checkpoint_rules: questions.map((question) => ({
    agency_question_id: question.id,
    enabled: values.enabledQuestionIds.includes(question.id),
  })),
});

const getNameError = (name: string): string => {
  let error = '';
  if (!name.trim()) {
    error = 'Enter a name for this checkpoint.';
  }
  return error;
};

type SettingCheckboxProps = {
  /** Unique within the dialog; ties the description to the checkbox so it's read out too. */
  id: string;
  label: string;
  /** Some agency questions have none. */
  description: string | null;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Checkbox page: the reason must be visible on screen. */
  disabled?: boolean;
};

/** One checkbox with its explanation underneath (Checkbox page: explanations go in FormHelperText). */
const SettingCheckbox = ({ id, label, description, checked, onChange, disabled }: SettingCheckboxProps) => {
  const descriptionId = `${id}-description`;
  return (
    <div>
      <FormControlLabel
        label={label}
        disabled={disabled}
        control={
          <Checkbox
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            slotProps={{
              input: { 'aria-describedby': description ? descriptionId : undefined }
            }}
          />
        }
      />
      {description && (
        <FormHelperText id={descriptionId} sx={{ mt: 0, ml: CHECKBOX_LABEL_INSET, mr: 0 }}>
          {description}
        </FormHelperText>
      )}
    </div>
  );
};

type CheckpointDialogProps = {
  open: boolean;
  /** The checkpoint being edited, or null to add a new one. */
  checkpoint: Checkpoint | null;
  /** Where the checkpoint is, shown under the title so it's clear where a new one goes. */
  locationName: string;
  /** The agency's questions, offered when adding. Editing uses the checkpoint's own rules. */
  agencyQuestions: AgencyQuestionSummary[];
  /** Sends the checkpoint to the API. The dialog closes when it succeeds. */
  onSave: (fields: CheckpointFields) => Promise<WebApiResult<Checkpoint>>;
  onClose: () => void;
};

/**
 * Add or edit a checkpoint. The settings and questions are checkboxes, not
 * switches, because nothing applies until Save (Switch page: a switch applies
 * immediately and never sits beside a Save button).
 *
 * A partner checkpoint (partner set) is run by the partner: its name,
 * description and settings are theirs, and the API keeps PIN and check-in off.
 * Only its questions can be changed here. Name and description are read-only
 * rather than disabled, so they stay legible and focusable (TextField page:
 * values the user must read but not edit); the settings checkboxes have no
 * read-only mode, so they're disabled, with the reason in an Alert.
 *
 * Name is validated when it loses focus and again on save. The API's own errors
 * go under Name or Description when they're about one of them, or above the
 * buttons otherwise. The form starts from `checkpoint` when it mounts, so callers
 * give this a new `key` each time it opens.
 */
const CheckpointDialog = ({
  open,
  checkpoint,
  locationName,
  agencyQuestions,
  onSave,
  onClose,
}: CheckpointDialogProps) => {
  const [values, setValues] = useState(() => getFormValues(checkpoint, agencyQuestions));
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // An error from the API that isn't about Name or Description.
  const [saveError, setSaveError] = useState('');
  // Below sm the dialog fills the screen (Dialog page). `fullScreen` is a prop, so it needs the hook.
  const isMobile = useMediaQuery(surepassTheme.breakpoints.down('sm'));

  let title = 'Add Checkpoint';
  let saveLabel = 'Add Checkpoint';
  let questions = agencyQuestions;
  if (checkpoint) {
    title = 'Edit Checkpoint';
    saveLabel = 'Save Changes';
    questions = checkpoint.checkpoint_rules.map((rule) => rule.agency_question);
  }
  const enabledCount = values.enabledQuestionIds.length;
  const partner = checkpoint?.partner ?? null;

  const toggleQuestion = (questionId: string, enabled: boolean) => {
    let enabledQuestionIds = values.enabledQuestionIds.filter((id) => id !== questionId);
    if (enabled) {
      enabledQuestionIds = [...enabledQuestionIds, questionId];
    }
    setValues({ ...values, enabledQuestionIds });
  };

  /** Shows the errors and moves focus to the first field with one. */
  const showFieldErrors = (nextNameError: string, nextDescriptionError: string) => {
    // Render the errors before moving focus, so the field is announced with its message.
    flushSync(() => {
      setNameError(nextNameError);
      setDescriptionError(nextDescriptionError);
    });
    if (nextNameError) {
      document.getElementById(NAME_ID)?.focus();
    } else if (nextDescriptionError) {
      document.getElementById(DESCRIPTION_ID)?.focus();
    }
  };

  const save = async () => {
    setIsSaving(true);
    setSaveError('');
    const result = await onSave(getCheckpointFields(values, questions));
    setIsSaving(false);
    if (result.error) {
      let nextNameError = '';
      let nextDescriptionError = '';
      const otherMessages: string[] = [];
      (result.errors ?? []).forEach((apiError) => {
        if (apiError.attribute === 'name') {
          nextNameError = apiError.message;
        } else if (apiError.attribute === 'description') {
          nextDescriptionError = apiError.message;
        } else {
          otherMessages.push(apiError.message);
        }
      });
      // No error objects at all (e.g. the network failed): the joined message is all there is.
      if (!result.errors?.length) {
        otherMessages.push(result.error);
      }
      setSaveError(otherMessages.join(' '));
      showFieldErrors(nextNameError, nextDescriptionError);
    } else {
      onClose();
    }
  };

  // Save stays enabled and says what's missing when clicked (Button page).
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    let error = '';
    if (!partner) {
      error = getNameError(values.name);
    }
    if (error) {
      showFieldErrors(error, descriptionError);
    } else {
      save();
    }
  };

  // Nothing closes the dialog while a save is in flight, so its result isn't lost.
  const close = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      aria-labelledby={TITLE_ID}
      // The paper is the form, so Enter in a field submits and the content keeps its own scrolling.
      slotProps={{ paper: { ...FORM_PAPER_PROPS, onSubmit: handleSubmit, sx: scrollbarStyles } }}
    >
      <CardHeader
        title={title}
        subheader={locationName}
        slotProps={{
          title: { id: TITLE_ID, component: 'h2' },
          // Dialog page: a subheader is body2.
          subheader: { variant: 'body2' },
          // Cancels MUI's negative margin on the action slot, so the header is 72px.
          action: { sx: { alignSelf: 'center', m: 0 } },
        }}
        action={
          // Tooltip page: a control that can be disabled is wrapped in a span, or the
          // tooltip can't open on it and MUI warns.
          <Tooltip title="Close" describeChild>
            <span>
              <IconButton aria-label="Close" onClick={close} disabled={isSaving}>
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {partner && (
            // Alert page: a condition that stays true while the checkpoint is a partner's,
            // so the status variant, in the neutral tone (it isn't good, bad or a warning).
            // role="status" so an info alert doesn't interrupt the reader.
            <Alert variant="status" color="default" severity="info" role="status">
              This checkpoint is integrated with {partner.name}, so its name, description and
              settings can&apos;t be changed here. You can still choose which questions it asks.
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              id={NAME_ID}
              label="Name"
              required={!partner}
              value={values.name}
              onChange={(event) => setValues({ ...values, name: event.target.value })}
              onBlur={() => {
                if (!partner) {
                  setNameError(getNameError(values.name));
                }
              }}
              error={Boolean(nameError)}
              helperText={nameError}
              autoComplete="off"
              fullWidth
              slotProps={{ input: { readOnly: Boolean(partner) } }}
            />
            <TextField
              id={DESCRIPTION_ID}
              label="Description"
              value={values.description}
              onChange={(event) => setValues({ ...values, description: event.target.value })}
              error={Boolean(descriptionError)}
              helperText={descriptionError}
              autoComplete="off"
              fullWidth
              slotProps={{ input: { readOnly: Boolean(partner) } }}
            />
          </Stack>

          {/* Checkbox page: a group is a fieldset with a legend. The legend holds the
              section heading: h3 under the dialog's h2, drawn at subtitle1 so it doesn't
              outsize the 20px dialog title (Heading page: keep the level, drop the size). */}
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" component="h3" sx={{ color: 'text.primary' }}>
                Settings
              </Typography>
            </FormLabel>
            <FormGroup sx={{ gap: 1 }}>
              <SettingCheckbox
                id="checkpoint-pin"
                label="Verifier authentication PIN"
                description="Require a verifier to enter their PIN during every verification."
                checked={values.pin_required}
                onChange={(checked) => setValues({ ...values, pin_required: checked })}
                disabled={Boolean(partner)}
              />
              <SettingCheckbox
                id="checkpoint-check-in"
                label="Track check-in and check-out"
                description="When checked, scans track user check-in and check-out. When unchecked, scans are recorded in your activity history without check-in or check-out status."
                checked={values.check_in_enabled}
                onChange={(checked) => setValues({ ...values, check_in_enabled: checked })}
                disabled={Boolean(partner)}
              />
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
                <Typography variant="subtitle1" component="h3" sx={{ color: 'text.primary' }}>
                  Additional questions
                </Typography>
                {questions.length > 0 && (
                  // Text page: caption is for counts.
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {enabledCount}/{questions.length} enabled
                  </Typography>
                )}
              </Stack>
            </FormLabel>
            {questions.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                This agency has no additional questions yet.
              </Typography>
            ) : (
              <FormGroup sx={{ gap: 1 }}>
                {questions.map((question) => (
                  <SettingCheckbox
                    key={question.id}
                    id={`checkpoint-question-${question.id}`}
                    label={question.question}
                    description={question.description}
                    checked={values.enabledQuestionIds.includes(question.id)}
                    onChange={(checked) => toggleQuestion(question.id, checked)}
                  />
                ))}
              </FormGroup>
            )}
          </FormControl>
        </Stack>
      </DialogContent>

      {/* Above the buttons rather than at the top, so it's seen however far the form is scrolled. */}
      {saveError && (
        <Alert severity="error" sx={{ mx: 2 }}>
          {saveError}
        </Alert>
      )}

      {/* Secondary then primary, right-aligned; equal width on mobile (Dialog page). */}
      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={isSaving}
          sx={{ flex: { xs: 1, sm: 'initial' } }}
        >
          Cancel
        </Button>
        {/* Button page: an in-flight action shows `loading`. */}
        <Button
          type="submit"
          variant="contained"
          loading={isSaving}
          sx={{ flex: { xs: 1, sm: 'initial' } }}
        >
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckpointDialog;
